// components/ProfitLossReport.jsx
import React, { useState } from 'react';
import { formatDate } from '../utils/dateUtils';
import { formatNumber } from '../utils/numberUtils';
import reportsAPI from '../api/reportsAPI';

const ProfitLossReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    storeId: ''
  });

  // Add print styles directly to the component
  const printStyles = `
    @media print {
      /* Hide navigation, sidebar, and other non-report elements */
      .sidebar, 
      .navbar, 
      .header, 
      .no-print, 
      .print\\:hidden,
      [class*="sidebar"],
      [class*="navbar"],
      [class*="header"] {
        display: none !important;
      }
      
      /* Ensure the report takes full width */
      .main-content,
      .content-area,
      [class*="main"],
      [class*="content"] {
        margin-left: 0 !important;
        width: 100% !important;
        padding: 0 !important;
      }
      
      /* Body and page setup */
      body {
        background: white !important;
        font-size: 12pt;
        line-height: 1.2;
      }
      
      /* Report container styling */
      .print-area {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        box-shadow: none !important;
        border: none !important;
      }
      
      /* Ensure good contrast for printing */
      * {
        color: black !important;
        background: white !important;
      }
      
      /* Improve table readability */
      table {
        border-collapse: collapse;
        width: 100%;
      }
      
      th, td {
        border: 1px solid #ddd;
        padding: 4px 8px;
      }
      
      th {
        background-color: #f8f8f8 !important;
        font-weight: bold;
      }
      
      /* Hide action buttons during print */
      .no-print {
        display: none !important;
      }
      
      /* Ensure cards don't break across pages */
      .break-inside-avoid {
        break-inside: avoid;
      }
      
      /* Page margins */
      @page {
        margin: 0.5in;
        size: letter;
      }
      
      /* Header styling for print */
      .print-header {
        border-bottom: 2px solid #000;
        margin-bottom: 20px;
        padding-bottom: 10px;
      }
    }
  `;

  // Inject print styles
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = printStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const generateReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportsAPI.getProfitLoss(filters);
      console.log('Backend Response:', response.data); // Debug log
      setReport(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handlePrint = () => {
    if (!report) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Profit & Loss Report - ${filters.startDate} to ${filters.endDate}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #000;
              line-height: 1.4;
            }
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 15px;
            }
            .summary-cards {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 25px;
            }
            .summary-card {
              border: 1px solid #ddd;
              padding: 15px;
              text-align: center;
              border-radius: 4px;
            }
            .summary-card h3 {
              margin: 0 0 8px 0;
              font-size: 14px;
              font-weight: bold;
            }
            .summary-card p {
              margin: 0;
              font-size: 18px;
              font-weight: bold;
            }
            .section {
              margin-bottom: 25px;
              page-break-inside: avoid;
            }
            .section-title {
              background: #f3f4f6;
              padding: 10px 15px;
              margin: 0 0 15px 0;
              border-left: 4px solid #000;
              font-size: 16px;
              font-weight: bold;
            }
            .details-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 20px;
            }
            .detail-card {
              border: 1px solid #ddd;
              padding: 0;
            }
            .detail-card-header {
              background: #f8f9fa;
              padding: 12px 15px;
              border-bottom: 1px solid #ddd;
              font-weight: bold;
            }
            .detail-card-body {
              padding: 15px;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              padding-bottom: 8px;
              border-bottom: 1px dashed #eee;
            }
            .detail-row:last-child {
              border-bottom: none;
              margin-bottom: 0;
              padding-bottom: 0;
            }
            .total-row {
              border-top: 2px solid #000;
              padding-top: 10px;
              margin-top: 10px;
              font-weight: bold;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
            }
            th, td {
              padding: 10px 12px;
              text-align: left;
              border: 1px solid #ddd;
            }
            th {
              background-color: #f8f9fa;
              font-weight: bold;
            }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .metrics-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              text-align: center;
            }
            .metric-value {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .metric-label {
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 15px; }
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>PROFIT & LOSS REPORT</h1>
            <h2>${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}</h2>
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>

          <!-- Summary Cards -->
          <div class="summary-cards">
            <div class="summary-card">
              <h3>Net Revenue</h3>
              <p>${formatNumber(report.summary.revenue.netRevenue)}</p>
            </div>
            <div class="summary-card">
              <h3>COGS</h3>
              <p>${formatNumber(report.summary.costOfGoodsSold.netCOGS)}</p>
            </div>
            <div class="summary-card">
              <h3>Gross Profit</h3>
              <p>${formatNumber(report.summary.grossProfit.amount)}</p>
            </div>
            <div class="summary-card">
              <h3>Gross Margin</h3>
              <p>${report.summary.grossProfit.margin.toFixed(1)}%</p>
            </div>
          </div>

          <!-- Revenue & COGS Details -->
          <div class="details-grid">
            <!-- Revenue -->
            <div class="detail-card">
              <div class="detail-card-header">Revenue Breakdown</div>
              <div class="detail-card-body">
                <div class="detail-row">
                  <span>Total Sales</span>
                  <span>${formatNumber(report.summary.revenue.totalRevenue)}</span>
                </div>
                <div class="detail-row">
                  <span>Returns</span>
                  <span>-${formatNumber(report.summary.revenue.totalReturns)}</span>
                </div>
                <div class="detail-row">
                  <span>Discounts</span>
                  <span>-${formatNumber(report.summary.revenue.totalDiscounts)}</span>
                </div>
                <div class="detail-row total-row">
                  <strong>Net Revenue</strong>
                  <strong>${formatNumber(report.summary.revenue.netRevenue)}</strong>
                </div>
              </div>
            </div>

            <!-- COGS -->
            <div class="detail-card">
              <div class="detail-card-header">Cost of Goods Sold</div>
              <div class="detail-card-body">
                <div class="detail-row">
                  <span>COGS of Sold Items</span>
                  <span>${formatNumber(report.summary.costOfGoodsSold.totalCOGS)}</span>
                </div>
                <div class="detail-row">
                  <span>COGS of Returned Items</span>
                  <span>-${formatNumber(report.summary.costOfGoodsSold.totalReturnsCOGS)}</span>
                </div>
                <div class="detail-row total-row">
                  <strong>Net COGS</strong>
                  <strong>${formatNumber(report.summary.costOfGoodsSold.netCOGS)}</strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Gross Profit Summary -->
          <div class="section">
            <div class="section-title">Gross Profit Summary</div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 0 15px;">
              <div>
                <div style="font-size: 24px; font-weight: bold; color: #1e40af;">
                  ${formatNumber(report.summary.grossProfit.amount)}
                </div>
                <div style="font-size: 14px; color: #666;">
                  Gross Profit Margin: ${report.summary.grossProfit.margin.toFixed(1)}%
                </div>
              </div>
              <div style="text-align: right;">
                <div style="font-size: 14px; color: #666;">
                  Net Revenue: ${formatNumber(report.summary.revenue.netRevenue)}
                </div>
                <div style="font-size: 14px; color: #666;">
                  COGS: ${formatNumber(report.summary.costOfGoodsSold.netCOGS)}
                </div>
              </div>
            </div>
          </div>

          <!-- Performance Metrics -->
          <div class="section">
            <div class="section-title">Performance Metrics</div>
            <div class="metrics-grid">
              <div>
                <div class="metric-value">${report.summary.performance.totalOrders}</div>
                <div class="metric-label">Total Orders</div>
              </div>
              <div>
                <div class="metric-value">${formatNumber(report.summary.performance.averageOrderValue)}</div>
                <div class="metric-label">Avg Order Value</div>
              </div>
              <div>
                <div class="metric-value">${report.summary.performance.returnRate.toFixed(1)}%</div>
                <div class="metric-label">Return Rate</div>
              </div>
              <div>
                <div class="metric-value">${report.productPerformance.summary.totalProducts}</div>
                <div class="metric-label">Products Sold</div>
              </div>
            </div>
          </div>

          <!-- Top Products -->
          <div class="section">
            <div class="section-title">Top Performing Products</div>
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th class="text-right">Qty Sold</th>
                  <th class="text-right">Revenue</th>
                  <th class="text-right">Cost</th>
                  <th class="text-right">Profit</th>
                  <th class="text-right">Margin</th>
                </tr>
              </thead>
              <tbody>
                ${report.productPerformance.topProducts.map((product, index) => `
                  <tr>
                    <td>${product.productName}</td>
                    <td class="text-right">${formatNumber(product.quantitySold)}</td>
                    <td class="text-right">${formatNumber(product.totalRevenue)}</td>
                    <td class="text-right">${formatNumber(product.totalCost)}</td>
                    <td class="text-right"><strong>${formatNumber(product.grossProfit)}</strong></td>
                    <td class="text-right">${product.grossMargin.toFixed(1)}%</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleExport = () => {
    // Simple CSV export implementation
    if (!report) return;

    const csvContent = [
      ['Profit & Loss Report', '', '', ''],
      ['Period', `${filters.startDate} to ${filters.endDate}`, '', ''],
      ['Generated', new Date().toLocaleDateString(), '', ''],
      [],
      ['Revenue', 'Amount', 'Percentage', 'Notes'],
      ['Total Sales', report.summary.revenue.totalRevenue, '', ''],
      ['Returns', `-${report.summary.revenue.totalReturns}`, '', ''],
      ['Discounts', `-${report.summary.revenue.totalDiscounts}`, '', ''],
      ['Net Revenue', report.summary.revenue.netRevenue, '100%', ''],
      [],
      ['Cost of Goods Sold', 'Amount', 'Percentage', 'Notes'],
      ['COGS of Sold Items', report.summary.costOfGoodsSold.totalCOGS, '', ''],
      ['COGS of Returned Items', `-${report.summary.costOfGoodsSold.totalReturnsCOGS}`, '', ''],
      ['Net COGS', report.summary.costOfGoodsSold.netCOGS, `${((report.summary.costOfGoodsSold.netCOGS / report.summary.revenue.netRevenue) * 100).toFixed(1)}%`, ''],
      [],
      ['Gross Profit', 'Amount', 'Percentage', 'Notes'],
      ['Gross Profit', report.summary.grossProfit.amount, `${report.summary.grossProfit.margin.toFixed(1)}%`, ''],
      [],
      ['Performance Metrics', 'Value', '', ''],
      ['Total Orders', report.summary.performance.totalOrders, '', ''],
      ['Average Order Value', report.summary.performance.averageOrderValue, '', ''],
      ['Return Rate', `${report.summary.performance.returnRate.toFixed(1)}%`, '', ''],
      ['Total Products Sold', report.productPerformance.summary.totalProducts, '', ''],
      ['Total Quantity Sold', report.summary.retailMetrics.netQuantitySold, '', '']
    ];

    const csvString = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profit-loss-report-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!report) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold mb-6">Profit & Loss Report</h1>
          
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={generateReport}
                disabled={loading}
                className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="text-center text-gray-500 py-12">
            <p>Select a date range and generate your Profit & Loss report</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 print:p-0 print:m-0 print:max-w-none">
      <div className="bg-white rounded-lg shadow-md p-6 print:shadow-none print:rounded-none print:p-4 print-area">
        {/* Header */}
        <div className="flex justify-between items-start mb-6 print:mb-4 print-header">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 print:text-xl">Profit & Loss Report</h1>
            <p className="text-gray-600 print:text-sm">
              {formatDate(filters.startDate)} to {formatDate(filters.endDate)}
            </p>
            <p className="text-sm text-gray-500 print:text-xs">
              Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
            </p>
          </div>
          <div className="flex space-x-2 no-print">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Print Report
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
            >
              Export CSV
            </button>
            <button
              onClick={() => setReport(null)}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              New Report
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 break-inside-avoid">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 print:border print:p-2">
            <h3 className="text-sm font-medium text-green-800 print:text-xs">Net Revenue</h3>
            <p className="text-2xl font-bold text-green-900 print:text-xl">
              {formatNumber(report.summary.revenue.netRevenue)}
            </p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 print:border print:p-2">
            <h3 className="text-sm font-medium text-red-800 print:text-xs">COGS</h3>
            <p className="text-2xl font-bold text-red-900 print:text-xl">
              {formatNumber(report.summary.costOfGoodsSold.netCOGS)}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 print:border print:p-2">
            <h3 className="text-sm font-medium text-blue-800 print:text-xs">Gross Profit</h3>
            <p className="text-2xl font-bold text-blue-900 print:text-xl">
              {formatNumber(report.summary.grossProfit.amount)}
            </p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 print:border print:p-2">
            <h3 className="text-sm font-medium text-purple-800 print:text-xs">Gross Margin</h3>
            <p className="text-2xl font-bold text-purple-900 print:text-xl">
              {report.summary.grossProfit.margin.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 break-inside-avoid">
          {/* Revenue Section */}
          <div className="border border-gray-200 rounded-lg print:border print:rounded-none">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-100 print:px-2 print:py-1">
              <h3 className="text-lg font-semibold text-gray-900 print:text-base">Revenue</h3>
            </div>
            <div className="p-4 space-y-3 print:p-2 print:space-y-1">
              <div className="flex justify-between">
                <span className="print:text-sm">Total Sales</span>
                <span className="font-medium print:text-sm">{formatNumber(report.summary.revenue.totalRevenue)}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span className="print:text-sm">Returns</span>
                <span className="print:text-sm">-{formatNumber(report.summary.revenue.totalReturns)}</span>
              </div>
              <div className="flex justify-between text-orange-600">
                <span className="print:text-sm">Discounts</span>
                <span className="print:text-sm">-{formatNumber(report.summary.revenue.totalDiscounts)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2 font-bold print:pt-1">
                <span className="print:text-sm">Net Revenue</span>
                <span className="print:text-sm">{formatNumber(report.summary.revenue.netRevenue)}</span>
              </div>
            </div>
          </div>

          {/* COGS Section */}
          <div className="border border-gray-200 rounded-lg print:border print:rounded-none">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-100 print:px-2 print:py-1">
              <h3 className="text-lg font-semibold text-gray-900 print:text-base">Cost of Goods Sold</h3>
            </div>
            <div className="p-4 space-y-3 print:p-2 print:space-y-1">
              <div className="flex justify-between">
                <span className="print:text-sm">COGS of Sold Items</span>
                <span className="font-medium print:text-sm">{formatNumber(report.summary.costOfGoodsSold.totalCOGS)}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span className="print:text-sm">COGS of Returned Items</span>
                <span className="print:text-sm">-{formatNumber(report.summary.costOfGoodsSold.totalReturnsCOGS)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2 font-bold print:pt-1">
                <span className="print:text-sm">Net COGS</span>
                <span className="print:text-sm">{formatNumber(report.summary.costOfGoodsSold.netCOGS)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Gross Profit Summary */}
        <div className="border border-gray-200 rounded-lg mb-6 break-inside-avoid print:border print:rounded-none">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-100 print:px-2 print:py-1">
            <h3 className="text-lg font-semibold text-gray-900 print:text-base">Gross Profit Summary</h3>
          </div>
          <div className="p-4 print:p-2">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-2xl font-bold text-blue-900 print:text-xl">
                  {formatNumber(report.summary.grossProfit.amount)}
                </p>
                <p className="text-sm text-gray-600 print:text-xs">
                  Gross Profit Margin: {report.summary.grossProfit.margin.toFixed(1)}%
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 print:text-xs">Net Revenue: {formatNumber(report.summary.revenue.netRevenue)}</p>
                <p className="text-sm text-gray-600 print:text-xs">COGS: {formatNumber(report.summary.costOfGoodsSold.netCOGS)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Retail Metrics */}
        <div className="border border-gray-200 rounded-lg mb-6 break-inside-avoid print:border print:rounded-none">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-100 print:px-2 print:py-1">
            <h3 className="text-lg font-semibold text-gray-900 print:text-base">Retail Metrics</h3>
          </div>
          <div className="p-4 print:p-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:gap-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 print:text-xl">{report.summary.retailMetrics.netQuantitySold}</p>
                <p className="text-sm text-gray-600 print:text-xs">Net Quantity Sold</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 print:text-xl">{formatNumber(report.summary.retailMetrics.averageProductCost)}</p>
                <p className="text-sm text-gray-600 print:text-xs">Avg Product Cost</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 print:text-xl">{formatNumber(report.summary.retailMetrics.averageSellingPrice)}</p>
                <p className="text-sm text-gray-600 print:text-xs">Avg Selling Price</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 print:text-xl">{formatNumber(report.summary.retailMetrics.averageProfitPerUnit)}</p>
                <p className="text-sm text-gray-600 print:text-xs">Avg Profit Per Unit</p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="border border-gray-200 rounded-lg mb-6 break-inside-avoid print:border print:rounded-none">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-100 print:px-2 print:py-1">
            <h3 className="text-lg font-semibold text-gray-900 print:text-base">Performance Metrics</h3>
          </div>
          <div className="p-4 print:p-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:gap-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 print:text-xl">{report.summary.performance.totalOrders}</p>
                <p className="text-sm text-gray-600 print:text-xs">Total Orders</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 print:text-xl">{formatNumber(report.summary.performance.averageOrderValue)}</p>
                <p className="text-sm text-gray-600 print:text-xs">Avg Order Value</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 print:text-xl">{report.summary.performance.returnRate.toFixed(1)}%</p>
                <p className="text-sm text-gray-600 print:text-xs">Return Rate</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 print:text-xl">{report.productPerformance.summary.totalProducts}</p>
                <p className="text-sm text-gray-600 print:text-xs">Products Sold</p>
              </div>
            </div>
          </div>
        </div>

        {/* Top Products */}
        <div className="border border-gray-200 rounded-lg break-inside-avoid print:border print:rounded-none">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-100 print:px-2 print:py-1">
            <h3 className="text-lg font-semibold text-gray-900 print:text-base">Top Performing Products</h3>
          </div>
          <div className="p-4 print:p-2">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 print:text-xs">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase print:px-2 print:py-1">Product</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase print:px-2 print:py-1">Qty Sold</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase print:px-2 print:py-1">Revenue</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase print:px-2 print:py-1">Cost</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase print:px-2 print:py-1">Profit</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase print:px-2 print:py-1">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {report.productPerformance.topProducts.map((product, index) => (
                    <tr key={product._id || index}>
                      <td className="px-4 py-2 text-sm print:px-2 print:py-1 print:text-xs">{product.productName}</td>
                      <td className="px-4 py-2 text-sm text-right print:px-2 print:py-1 print:text-xs">{formatNumber(product.quantitySold)}</td>
                      <td className="px-4 py-2 text-sm text-right print:px-2 print:py-1 print:text-xs">{formatNumber(product.totalRevenue)}</td>
                      <td className="px-4 py-2 text-sm text-right print:px-2 print:py-1 print:text-xs">{formatNumber(product.totalCost)}</td>
                      <td className="px-4 py-2 text-sm text-right font-medium print:px-2 print:py-1 print:text-xs">
                        {formatNumber(product.grossProfit)}
                      </td>
                      <td className="px-4 py-2 text-sm text-right print:px-2 print:py-1 print:text-xs">
                        {product.grossMargin.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossReport;